import { TYPE_ICONS, TYPE_LABELS, destinationFor } from "@/constants/search";
import useDebouncedValue from "@/hooks/useDebouncedValue";
import {
  MIN_SEARCH_LENGTH,
  SearchResult,
  useGlobalSearchQuery,
} from "@/services/search";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const GlobalSearch: FC = () => {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedTerm = useDebouncedValue(term.trim(), 250);
  const isSearchable = debouncedTerm.length >= MIN_SEARCH_LENGTH;

  const { data: groups, isFetching } = useGlobalSearchQuery(debouncedTerm, {
    skip: !isSearchable,
  });

  // Navigation clavier à plat : l'utilisateur descend d'un résultat au suivant
  // sans se soucier des groupes qui les séparent visuellement.
  const flatResults = useMemo(
    () => (groups ?? []).flatMap((group) => group.results),
    [groups],
  );

  useEffect(() => setActiveIndex(-1), [debouncedTerm]);

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onShortcut);
    return () => document.removeEventListener("keydown", onShortcut);
  }, []);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const close = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const goTo = (result: SearchResult) => {
    const destination = destinationFor(result);
    if (!destination) return;
    close();
    setTerm("");
    navigate(destination);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") return close();

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!flatResults.length) return;
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex(
        (previous) =>
          (previous + step + flatResults.length) % flatResults.length,
      );
      return;
    }

    // Sans résultat sélectionné, Entrée ne fait rien : il n'y a pas de page
    // de résultats, seulement le panneau.
    if (event.key === "Enter") {
      event.preventDefault();
      if (activeIndex >= 0 && flatResults[activeIndex]) {
        goTo(flatResults[activeIndex]);
      }
    }
  };

  const showPanel = isOpen && isSearchable;

  return (
    <div
      ref={containerRef}
      className="position-relative d-none d-md-block ms-3"
      style={{ width: 320 }}
    >
      <div className="position-relative">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="position-absolute top-50 translate-middle-y text-500 ms-3"
        />
        <Form.Control
          ref={inputRef}
          type="search"
          value={term}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="global-search-panel"
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `global-search-option-${activeIndex}` : undefined
          }
          aria-label="Recherche globale"
          placeholder="Rechercher…"
          className="ps-5"
          onChange={(event) => {
            setTerm(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>

      {showPanel && (
        <div
          id="global-search-panel"
          role="listbox"
          aria-label="Résultats de recherche"
          className="position-absolute start-0 end-0 mt-1 bg-white dark__bg-1100 border rounded-3 shadow-sm overflow-auto"
          style={{ maxHeight: 420, zIndex: 1030 }}
        >
          {isFetching && !groups && (
            <div className="p-3 text-center text-600">
              <Spinner animation="border" size="sm" className="me-2" />
              Recherche…
            </div>
          )}

          {groups?.length === 0 && !isFetching && (
            <p className="p-3 mb-0 text-center text-600">Aucun résultat</p>
          )}

          {groups?.map((group) => (
            <div key={group.type} className="border-bottom">
              <p className="px-3 pt-2 pb-1 mb-0 fs--2 fw-bold text-600 text-uppercase">
                {TYPE_LABELS[group.type]}
              </p>
              {group.results.map((result) => {
                const index = flatResults.indexOf(result);
                return (
                  <button
                    key={`${result.type}-${result.id}`}
                    type="button"
                    id={`global-search-option-${index}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`btn btn-link d-flex align-items-center gap-2 w-100 text-start text-decoration-none px-3 py-2 ${
                      index === activeIndex ? "bg-200" : ""
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => goTo(result)}
                  >
                    <FontAwesomeIcon
                      icon={TYPE_ICONS[result.type]}
                      className="text-500 flex-shrink-0"
                    />
                    <span className="text-truncate">
                      <span className="text-900">{result.title}</span>
                      {result.subtitle && (
                        <span className="text-600 ms-2 fs--1">
                          {result.subtitle}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
